import { GoogleGenerativeAI } from "@google/generative-ai";

/**
 * Parses a game schedule screenshot using Google Gemini API.
 * 
 * @param {File} imageFile - The image file to process
 * @param {string} apiKey - Google Gemini API Key
 * @returns {Promise<Array>} Array of parsed game objects
 */
export const parseScheduleImage = async (imageFile, apiKey) => {
    // Check for API key (param > env var)
    const key = apiKey || import.meta.env.VITE_GEMINI_API_KEY;

    if (!key) {
        throw new Error("API Key is required");
    }

    const modelsToTry = [
        "gemini-3.1-flash-lite",
        "gemini-2.0-flash",
        "gemini-flash-latest",
        "gemini-pro-latest",
        "gemini-1.5-flash",
        "gemini-1.5-pro"
    ];

    console.log(`[Gemini Service] Using Key: ...${key.slice(-4)}`);


    for (const modelName of modelsToTry) {
        try {
            console.log(`Attempting to use model: ${modelName}`);
            const genAI = new GoogleGenerativeAI(key);
            const model = genAI.getGenerativeModel({ model: modelName });

            // Convert file to base64
            const base64Data = await fileToGenerativePart(imageFile);

            // Prepare the prompt
            const prompt = `
            You are a data entry assistant for a cricket club. 
            Analyze this image of a game schedule and extract the match details.
            
            Return a STRICT JSON array where each object has these fields:
            - "date": Date string (YYYY-MM-DD format). If year is missing, assume current year or next occurrence.
            - "time": Time string (HH:MM AM/PM format).
            - "homeTeam": Name of the home team. If not explicitly stated, infer from context "Home" column or left side.
            - "awayTeam": Name of the away team. If not explicitly stated, infer from context "Away" column or right side.
            - "venue": Name of the ground or location.
            
            If you cannot find a specific field, use null.
            Do not include markdown formatting like \`\`\`json. Just return the raw JSON array.
            `;

            // Generate content
            const result = await model.generateContent([prompt, base64Data]);
            const response = await result.response;
            const text = response.text();

            // Clean up response
            const cleanText = text.replace(/```json/g, '').replace(/```/g, '').trim();

            // Parse JSON
            try {
                const schedule = JSON.parse(cleanText);
                return Array.isArray(schedule) ? schedule : [];
            } catch (parseError) {
                console.warn(`Failed to parse JSON for ${modelName}:`, text);
                continue; // Try next model or throw if last
            }

        } catch (error) {
            console.warn(`Model ${modelName} failed:`, error.message);
            if (modelName === modelsToTry[modelsToTry.length - 1]) {
                // Last attempt failed
                console.error("Gemini API All Models Failed:", error);

                // Construct helpful error message
                let listModelsHelp = "Check console for details.";
                try {
                    // Attempt to help user debug by listing what IS available via REST
                    // (This is a best-effort debug helper)
                    console.log("Attempting to list available models for your key...");
                    const listResp = await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${key}`);
                    if (listResp.ok) {
                        const data = await listResp.json();
                        console.table(data.models?.map(m => ({
                            name: m.name,
                            methods: m.supportedGenerationMethods
                        })));
                        listModelsHelp = "Available models logged to console table.";
                    } else {
                        console.warn("Could not list models:", listResp.statusText);
                    }
                } catch (dbgErr) {
                    console.warn("Debug check failed:", dbgErr);
                }

                throw new Error(`Failed to process image. API Error: ${error.message}. ${listModelsHelp}`);
            }
            // Otherwise loop to next model
        }
    }
};

/**
 * Helper to convert File object to GoogleGenerativeAI compatible format
 */
async function fileToGenerativePart(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64String = reader.result.split(',')[1];
            resolve({
                inlineData: {
                    data: base64String,
                    mimeType: file.type
                }
            });
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}
